import { useCallback, useState } from 'react'

import { Box, Table, Text } from '@factorialco/deprecated-design-system'
import {
  ComplianceSnapshotFragment,
  FinanceFinancialDocumentComplianceExtraFieldsConfig,
} from 'generated/resources/graphql'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import ComplianceSnapshotSidepanel from 'modules/finance/components/Invoices/ComplianceSnapshotSidepanel'

import { ComplianceSnapshotsRow, ComplianceSnapshotsRowProps } from './_ComplianceSnapshotsRow'

interface ComplianceSnapshotsProps {
  complianceSnapshots: ComplianceSnapshotFragment[]
  complianceExtraFieldsConfigs?: FinanceFinancialDocumentComplianceExtraFieldsConfig[]
}

const ComplianceSnapshotsHeaderCell = ({ value }: { value: string }) => (
  <Text.Normal size="200" weight="medium">
    {value}
  </Text.Normal>
)

function buildComplianceSnapshotsRows({
  complianceSnapshots,
  onClick,
}: {
  complianceSnapshots: ComplianceSnapshotFragment[]
  onClick: (complianceSnapshotId: number) => void
}): ComplianceSnapshotsRowProps[] {
  const rows: ComplianceSnapshotsRowProps[] = []

  complianceSnapshots.forEach((complianceSnapshot) => {
    rows.push({
      complianceSnapshot,
      onClick,
    })
  })

  return rows
}

const ComplianceSnapshots = ({
  complianceSnapshots,
  complianceExtraFieldsConfigs,
}: ComplianceSnapshotsProps) => {
  const [selectedComplianceSnapshot, setSelectedComplianceSnapshot] =
    useState<ComplianceSnapshotFragment | null>(null)

  const handleClick = useCallback(
    (complianceSnapshotId: number) => {
      setSelectedComplianceSnapshot(
        complianceSnapshots.find(
          (complianceSnapshot) => complianceSnapshot.id === complianceSnapshotId
        ) ?? null
      )
    },
    [setSelectedComplianceSnapshot, complianceSnapshots]
  )

  const complianceSnapshotsRows = useUnsafeMemo(() => {
    return buildComplianceSnapshotsRows({
      complianceSnapshots,
      onClick: handleClick,
    })
  }, [complianceSnapshots, handleClick])

  const headers = [
    i18n.t('finance.sale_invoices.detail.compliance_snapshots.header.event'),
    i18n.t('finance.sale_invoices.detail.compliance_snapshots.header.resource'),
    i18n.t('finance.sale_invoices.detail.compliance_snapshots.header.snapshot_date'),
  ]

  return (
    <>
      <Table fullWidth style={{ overflowY: 'auto', height: 'auto' }} autoLayout>
        <Table.Head>
          <Table.Row>
            {headers.map((header, index) => (
              <Table.HeadCell
                key={index}
                paddingX={index === 0 ? 's4' : 's24'}
                ellipsis
                background="grey000White"
              >
                <Box style={index === 0 ? { paddingLeft: '20px' } : { paddingRight: '20px' }}>
                  <ComplianceSnapshotsHeaderCell value={header} />
                </Box>
              </Table.HeadCell>
            ))}
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {complianceSnapshotsRows.map((row, index) => (
            <ComplianceSnapshotsRow
              key={index}
              {...row}
              selected={
                selectedComplianceSnapshot
                  ? selectedComplianceSnapshot.id === row.complianceSnapshot.id
                  : false
              }
            />
          ))}
        </Table.Body>
      </Table>
      {selectedComplianceSnapshot && (
        <ComplianceSnapshotSidepanel
          complianceExtraFieldsConfigs={complianceExtraFieldsConfigs}
          complianceSnapshot={selectedComplianceSnapshot}
          onClose={() => setSelectedComplianceSnapshot(null)}
        />
      )}
    </>
  )
}

export default ComplianceSnapshots
